import React, { useEffect, useState } from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    IconButton,
    Menu,
    MenuItem,
    Button,
    Box,
    Switch,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Divider,
    useMediaQuery,
    useTheme,
    ListItemButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import { Link, useNavigate } from "react-router-dom";
import API from "../api/axiosInstance";
import { useAppTheme } from "../themes/ThemeContext";

interface MenuAnchorState {
    [title: string]: HTMLElement | null;
}

const Header: React.FC = () => {
    const [user, setUser] = useState<any>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menuAnchors, setMenuAnchors] = useState<MenuAnchorState>({});
    const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const { mode, toggleTheme } = useAppTheme();
    const navigate = useNavigate();

    const isTenantAdmin = user?.tenantId?.equals("00000000-0000-0000-0000-000000000001");

    useEffect(() => {
        const checkSession = async () => {
            const stored = localStorage.getItem('user');
            if (!stored) {
                setUser(null);
                return;
            }

            const parsed = JSON.parse(stored);
            const headers = {
                headers: {
                    authToken: parsed.user?.authtoken,
                    sessionId: parsed.user?.sessionId,
                },
            };

            try {
                const res = await API.get("/auth/validate-session", headers);
                if (res.data?.valid) {
                    setUser(parsed.user);
                } else {
                    throw new Error("Invalid session");
                }
            } catch (err) {
                console.warn("Session invalid or expired.");
                handleLogout();
            }
        };

        checkSession();
        const interval = setInterval(checkSession, 30000); // every 30 sec

        return () => clearInterval(interval);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("user");
        setUser(null);
        navigate("/login");
    };

    const handleChangePassword = () => {
        navigate("/auth/change-password");
    }

    const mainMenus = [
        {
            title: "Dashboard",
            links: [
                { label: "My Dashboard", to: "/dashboard" },
                { label: "Assigned Items", to: "/myapprovals" },
            ],
        },
        {
            title: "Tables",
            links: [
                { label: "View Tables", to: "/tables" },
                { label: "Manage Datasets", to: "/datasets" },
            ],
        },
        {
            title: "Forms",
            links: [
                { label: "View Forms", to: "/forms" },
                { label: "Manage Forms", to: "/forms/manage" },
            ],
        },
        {
            title: "Reports",
            links: [
                { label: "View Reports", to: "/reports" },
                { label: "Manage Reports", to: "/reports/manage" },
            ],
        },
        {
            title: "Config",
            links: [
                { label: "Workflows", to: "/approvals/config" },
                { label: "Security", to: "/security" },
                { label: "Automation Logs", to: "/automations/logs" },
                { label: "Automation Monitor", to: "/automations/monitoring" },
                { label: "Licensing", to: "/licensing" },
                ...(isTenantAdmin
                    ? [
                        { label: "Tenant Management", to: "/tenants" },
                        { label: "Partition Management", to: "/partition/management" },
                    ]
                    : []),
            ],
        },
        {
            title: "Help",
            links: [{ label: "Contact Us", to: "/contactus" }],
        },
    ];

    const userMenuItems = [
        { label: "Options", action: () => console.log("Options clicked") },
        { label: "Change Password", action: handleChangePassword },
        { label: "Logout", action: handleLogout },
    ];

    const openMenu = (title: string, event: React.MouseEvent<HTMLElement>) => {
        setMenuAnchors((prev) => ({ ...prev, [title]: event.currentTarget }));
    };

    const closeMenu = (title: string) => {
        setMenuAnchors((prev) => ({ ...prev, [title]: null }));
    };

    return (
        <>
            <AppBar
                position="fixed"
                sx={{
                    backgroundColor: theme.palette.background.default,
                    color: theme.palette.text.primary,
                }}
            >
                <Toolbar>
                    {isMobile && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            onClick={() => setDrawerOpen(true)}
                        >
                            <MenuIcon />
                        </IconButton>
                    )}

                    <Box display="flex" alignItems="center" sx={{ flexGrow: 1 }}>
                        <Link
                            to={user ? "/dashboard" : "/"}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                                color: "inherit",
                            }}
                        >
                            <img src="/logo.png" alt="Logo" style={{ height: 32, marginRight: 10 }} />
                            <Typography variant="h6" noWrap>
                                Next Era Office
                            </Typography>
                        </Link>
                    </Box>

                    {/* Authenticated Desktop Menu */}
                    {!isMobile && user && mainMenus.map((menu) => (
                        <Box key={menu.title} sx={{ ml: 2 }}>
                            <Button
                                color="inherit"
                                onClick={(e) => openMenu(menu.title, e)}
                            >
                                {menu.title}
                            </Button>
                            <Menu
                                anchorEl={menuAnchors[menu.title]}
                                open={Boolean(menuAnchors[menu.title])}
                                onClose={() => closeMenu(menu.title)}
                                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                                transformOrigin={{ vertical: "top", horizontal: "left" }}
                            >
                                {menu.links.map((link) => (
                                    <MenuItem
                                        key={link.to}
                                        component={Link}
                                        to={link.to}
                                        onClick={() => closeMenu(menu.title)}
                                    >
                                        {link.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    ))}

                    {/* User Profile */}
                    {user ? (
                        <Box>
                            <IconButton
                                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                                color="inherit"
                            >
                                <AccountCircle />
                            </IconButton>
                            <Menu
                                anchorEl={userMenuAnchor}
                                open={Boolean(userMenuAnchor)}
                                onClose={() => setUserMenuAnchor(null)}
                            >
                                <MenuItem disabled>{user.fullName || user.name}</MenuItem>
                                <MenuItem>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Typography variant="body2">Dark Mode</Typography>
                                        <Switch
                                            checked={mode === "dark"}
                                            onChange={toggleTheme}
                                            size="small"
                                        />
                                    </Box>
                                </MenuItem>
                                <Divider />
                                {userMenuItems.map((item) => (
                                    <MenuItem
                                        key={item.label}
                                        onClick={() => {
                                            setUserMenuAnchor(null);
                                            item.action();
                                        }}
                                    >
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>
                    ) : (
                        !isMobile && (
                            <>
                                <Button color="inherit" component={Link} to="/signup">Sign Up</Button>
                                <Button color="inherit" component={Link} to="/login">Log In</Button>
                            </>
                        )
                    )}
                </Toolbar>
            </AppBar>

            {/* MOBILE MENU */}
            <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <Box
                    width={260}
                    role="presentation"
                    onClick={() => setDrawerOpen(false)}
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        height: "100%",
                        color: theme.palette.text.primary,
                    }}
                >
                    <List>
                        {user ? (
                            <>
                                {mainMenus.map((menu) => (
                                    <Box key={menu.title} sx={{ mb: 1 }}>
                                        <ListItem>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {menu.title}
                                            </Typography>
                                        </ListItem>
                                        {menu.links.map((link) => (
                                            <ListItem key={link.to} disablePadding>
                                                <ListItemButton component={Link} to={link.to}>
                                                    <ListItemText primary={link.label} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                    </Box>
                                ))}

                                <Divider />
                                <ListItem>
                                    <Typography variant="subtitle1" fontWeight="bold">
                                        {user.fullName || user.name}
                                    </Typography>
                                </ListItem>
                                <ListItem>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Typography variant="body2">Dark Mode</Typography>
                                        <Switch
                                            checked={mode === "dark"}
                                            onChange={toggleTheme}
                                            size="small"
                                        />
                                    </Box>
                                </ListItem>
                                {userMenuItems.map((item) => (
                                    <ListItem key={item.label} disablePadding>
                                        <ListItemButton onClick={item.action}>
                                            <ListItemText primary={item.label} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </>
                        ) : (
                            <>
                                <ListItemButton component={Link} to="/signup">
                                    <ListItemText primary="Sign Up" />
                                </ListItemButton>
                                <ListItemButton component={Link} to="/login">
                                    <ListItemText primary="Log In" />
                                </ListItemButton>
                            </>
                        )}
                    </List>
                </Box>
            </Drawer>
        </>
    );
};

export default Header;
